<script setup lang="ts">
  import { ref, computed, watch } from 'vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
  import { Button } from '@/components/ui/button'
  import { Users, Calendar, Link, FileText } from 'lucide-vue-next'
  import {
    EmailTemplatesService,
    FormsService,
    QuestionnairesService,
    UsersService,
    ServicesService,
    CalendarsService,
    WordDocumentTemplatesService,
    type EmailTemplateList as EmailTemplate,
    type CustomerQuestionnaire,
    type CustomerForm,
    type TUser,
    type TService,
    type TCalendar,
    type TWordDocumentTemplate
  } from '@/types/generated'

  // AI Capability Constants
  const AI_CAPABILITIES = {
    ASSIGN_LAWYERS: 'ASSIGN_LAWYERS',
    SEND_BOOKING_LINKS: 'SEND_BOOKING_LINKS',
    SEND_EMAIL_TEMPLATES: 'SEND_EMAIL_TEMPLATES',
    CREATE_DOCUMENTS: 'CREATE_DOCUMENTS',
    CHANGE_STATUS: 'CHANGE_STATUS'
  } as const

  type AICapabilityType = (typeof AI_CAPABILITIES)[keyof typeof AI_CAPABILITIES]

  // Translation setup
  const { t } = useTranslation({
    en: {
      introText: 'The AI can',
      tooltipText: 'Click for more information about',
      and: ' and ',
      availableEmailTemplates: 'Available Email Templates',
      availableQuestionnaires: 'Available Questionnaires',
      availablePdfDocuments: 'Available PDF Documents',
      noEmailTemplatesAvailable: 'No email templates available',
      noQuestionnairesAvailable: 'No questionnaires available',
      noPdfDocumentsAvailable: 'No PDF documents available',
      naturalLanguageInstruction:
        'Use natural language to give the AI instructions about which email template to send with which questionnaires and document templates.',
      exampleStandard:
        'Send the Standard template with the Master Data questionnaire, as well as the Power of Attorney and Terms of Engagement.',
      exampleRejection:
        'Send the Mandate Rejection (Capacity) template, without questionnaires or document templates.',
      exampleLaborLaw:
        'Send the Labor Law template with the Master Data and Termination questionnaires, but without document templates.',
      clerkAssignmentInstruction:
        'Use natural language to give the AI instructions about how to assign clerks to cases.',
      exampleAssignSpecialist:
        'Assign Mr. Müller as clerk for terminations, Ms. Meyer for matters around severance payments.',
      exampleAssignWorkload:
        'For inheritance law matters, please assign either Ms. Schmidt or Mr. Becker. Depending on workload.',
      exampleAssignComplexity:
        'Randomly select one of the following clerks: Ms. Heine, Mr. Meier or Ms. Schmidt.',
      availableClerks: 'Available Clerks',
      noClerksAvailable: 'No clerks available',
      bookingLinksInstruction:
        'Use natural language to give the AI instructions about sending appointment booking links.',
      exampleBookingInheritance:
        'Send an appointment booking link for inheritance law with Mr. Müller.',
      exampleBookingEmployment:
        "Send an appointment booking link for the initial employment law consultation, doesn't matter with whom.",
      exampleBookingGeneral:
        'Please send an appointment booking link for the General Initial Consultation (Fee-based) with Ms. Schmidt or Mr. Becker.',
      availableServices: 'Available Services',
      availableConnectedCalendars: 'Available Connected Calendars',
      noServicesAvailable: 'No services available',
      noCalendarsAvailable: 'No calendars available',
      documentCreationInstruction:
        'Use natural language to give the AI instructions about creating documents.',
      exampleCreateInsuranceLetter:
        'Create the letter to the insurance company (traffic law) and prepare a coverage inquiry.',
      exampleCreateEmploymentDocs:
        'Prepare the documents for unfair dismissal action, as well as the coverage inquiry. The coverage inquiry only if the client has legal expenses insurance.',
      availableWordDocumentTemplates: 'Available Word Document Templates',
      noWordDocumentTemplatesAvailable: 'No document templates available',
      capabilities: {
        [AI_CAPABILITIES.ASSIGN_LAWYERS]: {
          label: 'clerks',
          before: 'assign ',
          after: '',
          title: 'Automatically assign clerks',
          description: 'The AI can automatically assign the right clerk based on various criteria:',
          criteria: {
            expertise: 'Legal area and expertise',
            workload: 'Current workload',
            complexity: 'Case complexity',
            availability: 'Availability'
          },
          buttons: {
            showRules: 'Show assignment rules',
            documentation: 'Documentation'
          }
        },
        [AI_CAPABILITIES.SEND_BOOKING_LINKS]: {
          label: 'booking links',
          before: 'send ',
          after: '',
          title: 'Send booking links',
          description: 'Automatically send appointment booking links to clients:',
          triggers: {
            title: 'Automatic triggers:',
            afterAssignment: 'After case assignments',
            statusChanges: 'On status changes',
            reminders: 'Time-based reminders'
          },
          buttons: {
            editTemplates: 'Edit link templates',
            settings: 'Settings'
          }
        },
        [AI_CAPABILITIES.SEND_EMAIL_TEMPLATES]: {
          label: 'email templates',
          before: 'send ',
          after: ' with questionnaires and documents',
          title: 'Automatically send email templates',
          description: 'Intelligent email system with personalized templates:',
          types: {
            welcome: 'Welcome emails',
            welcomeDesc: 'Automatically for new cases',
            statusUpdates: 'Status updates',
            statusDesc: 'For important changes',
            reminders: 'Reminders',
            remindersDesc: 'Appointment reminders & deadlines',
            completion: 'Completion',
            completionDesc: 'Case completion notifications'
          },
          buttons: {
            manageTemplates: 'Manage templates',
            configureTriggers: 'Configure triggers'
          }
        },
        [AI_CAPABILITIES.CREATE_DOCUMENTS]: {
          label: 'documents',
          before: 'create ',
          after: '',
          title: 'Automatically create documents',
          description: 'AI-powered document creation for various legal areas:',
          types: {
            contracts: 'Contracts & agreements',
            pleadings: 'Pleadings & motions',
            reports: 'Reports & opinions'
          },
          status: {
            active: 'Active',
            beta: 'Beta'
          },
          buttons: {
            showTemplates: 'Show templates',
            configureAutomation: 'Configure automation'
          }
        },
        [AI_CAPABILITIES.CHANGE_STATUS]: {
          label: 'case status',
          before: 'change ',
          after: '',
          title: 'Automatically change case status',
          description: 'Automatic status updates based on workflow rules:',
          automationsTitle: 'Current automations:',
          rules: {
            newToInProgress: 'New → In Progress',
            newToInProgressTrigger: 'On clerk assignment',
            inProgressToWaiting: 'In Progress → Waiting',
            inProgressToWaitingTrigger: 'On client request',
            waitingToClosed: 'Waiting → Completed',
            waitingToClosedTrigger: 'After 30 days'
          },
          buttons: {
            editRules: 'Edit status rules',
            showHistory: 'Show history'
          }
        }
      }
    },
    de: {
      introText: 'Die KI kann',
      tooltipText: 'Klicken für mehr Informationen über',
      and: ' und ',
      availableEmailTemplates: 'Verfügbare E-Mail-Vorlagen',
      availableQuestionnaires: 'Verfügbare Fragebögen',
      availablePdfDocuments: 'Verfügbare PDF-Dokumente',
      noEmailTemplatesAvailable: 'Keine E-Mail-Vorlagen verfügbar',
      noQuestionnairesAvailable: 'Keine Fragebögen verfügbar',
      noPdfDocumentsAvailable: 'Keine PDF-Dokumente verfügbar',
      naturalLanguageInstruction:
        'Nutzen Sie natürliche Sprache, um der KI Anweisungen darüber zu geben welche E-Mail-Vorlage sie mit welchen Fragebögen bzw. Dokumentenvorlagen versenden soll.',
      exampleStandard:
        'Versende die Vorlage Standard mit dem Stammdaten Fragebogen, sowie der Vollmacht und den Mandatsbedingungen.',
      exampleRejection:
        'Versende die Vorlage Mandatsablehnung (Kapazität), ohne Fragebögen bzw. Dokumentenvorlagen.',
      exampleLaborLaw:
        'Versende die Vorlage Arbeitsrecht mit den Fragebögen Stammdaten und Kündigung, allerdings ohne Dokumentenvorlagen.',
      clerkAssignmentInstruction:
        'Nutzen Sie natürliche Sprache, um der KI Anweisungen zur Sachbearbeiter-Zuweisung zu geben.',
      exampleAssignSpecialist:
        'Weise Herrn Müller als Sachbearbeiter bei Kündigungen zu, Frau Meyer bei Sachen rund um Abfindungen.',
      exampleAssignWorkload:
        'Bei Angelegenheiten im Erbrecht bitte entweder Frau Schmidt, oder Herrn Becker zuweisen. Je nach Arbeitsbelastung.',
      exampleAssignComplexity:
        'Wähle zufällig einen der folgenden Bearbeiter aus: Frau Heine, Herr Meier oder Frau Schmidt.',
      availableClerks: 'Verfügbare Sachbearbeiter',
      noClerksAvailable: 'Keine Sachbearbeiter verfügbar',
      bookingLinksInstruction:
        'Nutzen Sie natürliche Sprache, um der KI Anweisungen zum Versenden von Terminbuchungslinks zu geben.',
      exampleBookingInheritance:
        'Sende einen Terminbuchungslink für Erbrecht bei Herrn Müller raus.',
      exampleBookingEmployment:
        'Versende einen Terminbuchungslink für das Erstgespräch Arbeitsrecht, bei wem ist egal.',
      exampleBookingGeneral:
        'Schicke bitte einen Terminbuchungslink für das Allgemeine Erstgespräch (Kostenpflichtig) bei Frau Schmidt, oder Herrn Becker raus.',
      availableServices: 'Verfügbare Services',
      availableConnectedCalendars: 'Verfügbare verbundene Kalender',
      noServicesAvailable: 'Keine Services verfügbar',
      noCalendarsAvailable: 'Keine Kalender verfügbar',
      documentCreationInstruction:
        'Nutzen Sie natürliche Sprache, um der KI Anweisungen zur Dokumentenerstellung zu geben.',
      exampleCreateInsuranceLetter:
        'Erstelle das Anschreiben an die Versicherung (Verkehrsrecht) und bereite eine Deckungsanfrage vor.',
      exampleCreateEmploymentDocs:
        'Bereite die Dokumente Kündigungsschutzklage, sowie die Deckungsanfrage vor. Die Deckungsanfrage allerdings nur dann, wenn der Mandant über eine Rechtschutzversicherung verfügt.',
      availableWordDocumentTemplates: 'Verfügbare Dokumentenvorlagen',
      noWordDocumentTemplatesAvailable: 'Keine Dokumentvorlagen verfügbar',
      capabilities: {
        [AI_CAPABILITIES.ASSIGN_LAWYERS]: {
          label: 'Sachbearbeiter',
          before: '',
          after: ' zuweisen',
          title: 'Sachbearbeiter automatisch zuweisen',
          description:
            'Die KI kann automatisch den passenden Sachbearbeiter basierend auf verschiedenen Kriterien zuweisen:',
          criteria: {
            expertise: 'Rechtsgebiet und Expertise',
            workload: 'Aktuelle Arbeitsauslastung',
            complexity: 'Fallkomplexität',
            availability: 'Verfügbarkeit'
          },
          buttons: {
            showRules: 'Zuweisungsregeln anzeigen',
            documentation: 'Dokumentation'
          }
        },
        [AI_CAPABILITIES.SEND_BOOKING_LINKS]: {
          label: 'Terminbuchungslinks',
          before: '',
          after: ' senden',
          title: 'Terminbuchungslinks versenden',
          description: 'Automatisches Versenden von Terminbuchungslinks an Mandanten:',
          triggers: {
            title: 'Automatische Auslöser:',
            afterAssignment: 'Nach Fallzuweisungen',
            statusChanges: 'Bei Status-Änderungen',
            reminders: 'Zeitgesteuerte Erinnerungen'
          },
          buttons: {
            editTemplates: 'Link-Vorlagen bearbeiten',
            settings: 'Einstellungen'
          }
        },
        [AI_CAPABILITIES.SEND_EMAIL_TEMPLATES]: {
          label: 'E-Mail-Vorlagen',
          before: '',
          after: ' mit Fragebögen und Dokumenten versenden',
          title: 'E-Mail-Vorlagen automatisch versenden',
          description: 'Intelligentes E-Mail-System mit personalisierten Vorlagen:',
          types: {
            welcome: 'Begrüßungsmails',
            welcomeDesc: 'Automatisch bei neuen Fällen',
            statusUpdates: 'Status-Updates',
            statusDesc: 'Bei wichtigen Änderungen',
            reminders: 'Erinnerungen',
            remindersDesc: 'Terminerinnerungen & Fristen',
            completion: 'Abschluss',
            completionDesc: 'Fallabschlussmitteilungen'
          },
          buttons: {
            manageTemplates: 'Vorlagen verwalten',
            configureTriggers: 'Auslöser konfigurieren'
          }
        },
        [AI_CAPABILITIES.CREATE_DOCUMENTS]: {
          label: 'Dokumente',
          before: '',
          after: ' erstellen',
          title: 'Dokumente automatisch erstellen',
          description: 'KI-gestützte Dokumenterstellung für verschiedene Rechtsgebiete:',
          types: {
            contracts: 'Verträge & Vereinbarungen',
            pleadings: 'Schriftsätze & Anträge',
            reports: 'Berichte & Gutachten'
          },
          status: {
            active: 'Aktiv',
            beta: 'Beta'
          },
          buttons: {
            showTemplates: 'Vorlagen anzeigen',
            configureAutomation: 'Automatisierung konfigurieren'
          }
        },
        [AI_CAPABILITIES.CHANGE_STATUS]: {
          label: 'Status',
          before: 'den ',
          after: ' der Anfrage ändern',
          title: 'Fallstatus automatisch ändern',
          description: 'Automatische Status-Updates basierend auf Workflow-Regeln:',
          automationsTitle: 'Aktuelle Automatisierungen:',
          rules: {
            newToInProgress: 'Neu → In Bearbeitung',
            newToInProgressTrigger: 'Bei Sachbearbeiter-Zuweisung',
            inProgressToWaiting: 'In Bearbeitung → Warten',
            inProgressToWaitingTrigger: 'Bei Mandantenanfrage',
            waitingToClosed: 'Warten → Abgeschlossen',
            waitingToClosedTrigger: 'Nach 30 Tagen'
          },
          buttons: {
            editRules: 'Status-Regeln bearbeiten',
            showHistory: 'Verlauf anzeigen'
          }
        }
      }
    }
  })

  const selectedCapability = ref<AICapabilityType | null>(null)
  const isDialogOpen = ref(false)

  // Data refs for email templates functionality
  const emailTemplates = ref<EmailTemplate[]>([])
  const allQuestionnaires = ref<CustomerQuestionnaire[]>([])
  const allForms = ref<CustomerForm[]>([])
  const isLoadingData = ref(false)

  // Data refs for clerks functionality
  const availableClerks = ref<TUser[]>([])
  const isLoadingClerks = ref(false)

  // Data refs for booking links functionality
  const availableServices = ref<TService[]>([])
  const availableCalendars = ref<TCalendar[]>([])
  const isLoadingBookingData = ref(false)

  // Data refs for document templates functionality
  const availableWordDocumentTemplates = ref<TWordDocumentTemplate[]>([])
  const isLoadingDocumentData = ref(false)

  // Data fetching functions
  const fetchEmailTemplates = async () => {
    try {
      const response = await EmailTemplatesService.emailTemplatesList({
        limit: 100 // Get all templates
      })
      emailTemplates.value = response.results as EmailTemplate[]
    } catch (error) {
      console.error('Failed to fetch email templates:', error)
    }
  }

  const fetchAllQuestionnaires = async () => {
    try {
      const response = await QuestionnairesService.questionnairesList({
        limit: 100 // Get all questionnaires
      })
      allQuestionnaires.value = response.results as CustomerQuestionnaire[]
    } catch (error) {
      console.error('Failed to fetch questionnaires:', error)
    }
  }

  const fetchAllForms = async () => {
    try {
      const response = await FormsService.formsList({
        tag: 'MASTER',
        limit: 100 // Get all forms
      })
      allForms.value = response.results as CustomerForm[]
    } catch (error) {
      console.error('Failed to fetch forms:', error)
    }
  }

  const fetchAvailableClerks = async () => {
    try {
      const response = await UsersService.usersList({
        limit: 100 // Get all users
      })
      availableClerks.value = response.results as TUser[]
    } catch (error) {
      console.error('Failed to fetch clerks:', error)
    }
  }

  const fetchAvailableServices = async () => {
    try {
      const response = await ServicesService.servicesList({
        limit: 100 // Get all services
      })
      availableServices.value = response.results as TService[]
    } catch (error) {
      console.error('Failed to fetch services:', error)
    }
  }

  const fetchAvailableCalendars = async () => {
    try {
      const response = await CalendarsService.calendarsList({
        limit: 100 // Get all calendars
      })
      availableCalendars.value = response.results as TCalendar[]
    } catch (error) {
      console.error('Failed to fetch calendars:', error)
    }
  }

  const fetchAvailableWordDocumentTemplates = async () => {
    try {
      const response = await WordDocumentTemplatesService.wordDocumentTemplatesList({
        limit: 100, // Get all templates
        status: 'approved' // Only fetch approved templates
      })
      availableWordDocumentTemplates.value = response.results as TWordDocumentTemplate[]
    } catch (error) {
      console.error('Failed to fetch word document templates:', error)
    }
  }

  const loadEmailTemplateData = async () => {
    if (isLoadingData.value) return

    isLoadingData.value = true
    try {
      await Promise.all([fetchEmailTemplates(), fetchAllQuestionnaires(), fetchAllForms()])
    } finally {
      isLoadingData.value = false
    }
  }

  const loadClerksData = async () => {
    if (isLoadingClerks.value) return

    isLoadingClerks.value = true
    try {
      await fetchAvailableClerks()
    } finally {
      isLoadingClerks.value = false
    }
  }

  const loadBookingData = async () => {
    if (isLoadingBookingData.value) return

    isLoadingBookingData.value = true
    try {
      await Promise.all([fetchAvailableServices(), fetchAvailableCalendars()])
    } finally {
      isLoadingBookingData.value = false
    }
  }

  const loadDocumentData = async () => {
    if (isLoadingDocumentData.value) return

    isLoadingDocumentData.value = true
    try {
      await fetchAvailableWordDocumentTemplates()
    } finally {
      isLoadingDocumentData.value = false
    }
  }

  // Computed array of capabilities with translations
  const aiCapabilities = computed(() => [
    {
      id: AI_CAPABILITIES.ASSIGN_LAWYERS,
      key: t(`capabilities.${AI_CAPABILITIES.ASSIGN_LAWYERS}.label`),
      before: t(`capabilities.${AI_CAPABILITIES.ASSIGN_LAWYERS}.before`),
      after: t(`capabilities.${AI_CAPABILITIES.ASSIGN_LAWYERS}.after`),
      title: t(`capabilities.${AI_CAPABILITIES.ASSIGN_LAWYERS}.title`)
    },
    {
      id: AI_CAPABILITIES.SEND_BOOKING_LINKS,
      key: t(`capabilities.${AI_CAPABILITIES.SEND_BOOKING_LINKS}.label`),
      before: t(`capabilities.${AI_CAPABILITIES.SEND_BOOKING_LINKS}.before`),
      after: t(`capabilities.${AI_CAPABILITIES.SEND_BOOKING_LINKS}.after`),
      title: t(`capabilities.${AI_CAPABILITIES.SEND_BOOKING_LINKS}.title`)
    },
    {
      id: AI_CAPABILITIES.SEND_EMAIL_TEMPLATES,
      key: t(`capabilities.${AI_CAPABILITIES.SEND_EMAIL_TEMPLATES}.label`),
      before: t(`capabilities.${AI_CAPABILITIES.SEND_EMAIL_TEMPLATES}.before`),
      after: t(`capabilities.${AI_CAPABILITIES.SEND_EMAIL_TEMPLATES}.after`),
      title: t(`capabilities.${AI_CAPABILITIES.SEND_EMAIL_TEMPLATES}.title`)
    },
    {
      id: AI_CAPABILITIES.CREATE_DOCUMENTS,
      key: t(`capabilities.${AI_CAPABILITIES.CREATE_DOCUMENTS}.label`),
      before: t(`capabilities.${AI_CAPABILITIES.CREATE_DOCUMENTS}.before`),
      after: t(`capabilities.${AI_CAPABILITIES.CREATE_DOCUMENTS}.after`),
      title: t(`capabilities.${AI_CAPABILITIES.CREATE_DOCUMENTS}.title`)
    },
    {
      id: AI_CAPABILITIES.CHANGE_STATUS,
      key: t(`capabilities.${AI_CAPABILITIES.CHANGE_STATUS}.label`),
      before: t(`capabilities.${AI_CAPABILITIES.CHANGE_STATUS}.before`),
      after: t(`capabilities.${AI_CAPABILITIES.CHANGE_STATUS}.after`),
      title: t(`capabilities.${AI_CAPABILITIES.CHANGE_STATUS}.title`)
    }
  ])

  const handleCapabilityClick = async (capabilityId: AICapabilityType) => {
    selectedCapability.value = capabilityId
    isDialogOpen.value = true

    // Load data for specific capabilities
    if (capabilityId === AI_CAPABILITIES.SEND_EMAIL_TEMPLATES) {
      await loadEmailTemplateData()
    } else if (capabilityId === AI_CAPABILITIES.ASSIGN_LAWYERS) {
      await loadClerksData()
    } else if (capabilityId === AI_CAPABILITIES.SEND_BOOKING_LINKS) {
      await loadBookingData()
    } else if (capabilityId === AI_CAPABILITIES.CREATE_DOCUMENTS) {
      await loadDocumentData()
    }
  }

  const getSelectedCapabilityData = () => {
    return aiCapabilities.value.find((cap) => cap.id === selectedCapability.value)
  }
</script>

<template>
  <div class="text-sm text-gray-600">
    {{ t('introText') }}
    <template
      v-for="(capability, index) in aiCapabilities"
      :key="capability.id"
    >
      {{ capability.before }}
      <span
        class="inline-flex cursor-pointer items-center rounded-md border border-gray-200 bg-white px-2 py-0.5
          text-xs font-medium text-gray-700 shadow-sm transition-all hover:border-gray-300 hover:bg-gray-50"
        :title="`${t('tooltipText')}: ${capability.key}`"
        @click="handleCapabilityClick(capability.id)"
      >
        {{ capability.key }}
      </span>
      {{ capability.after }}
      <span v-if="index < aiCapabilities.length - 2">,</span>
      <span v-else-if="index === aiCapabilities.length - 2">{{ t('and') }}</span>
      <span v-else>.</span>
    </template>
  </div>

  <!-- Dialog for detailed capability information -->
  <Dialog v-model:open="isDialogOpen">
    <DialogContent class="max-h-[80vh] max-w-2xl overflow-y-auto">
      <DialogHeader>
        <DialogTitle>{{ getSelectedCapabilityData()?.title }}</DialogTitle>
      </DialogHeader>
      <div class="py-4">
        <!-- Assign Lawyers -->
        <div
          v-if="selectedCapability === AI_CAPABILITIES.ASSIGN_LAWYERS"
          class="space-y-4"
        >
          <div class="space-y-3 text-sm text-gray-600">
            <p>
              {{ t('clerkAssignmentInstruction') }}
            </p>

            <div class="space-y-2 rounded-md bg-gray-50 p-3 text-xs">
              <p class="font-medium">{{ t('exampleAssignSpecialist') }}</p>

              <hr class="border-gray-300" />

              <p class="font-medium">{{ t('exampleAssignWorkload') }}</p>

              <hr class="border-gray-300" />

              <p class="font-medium">{{ t('exampleAssignComplexity') }}</p>
            </div>
          </div>

          <div
            v-if="isLoadingClerks"
            class="flex justify-center py-4"
          >
            <div
              class="h-6 w-6 animate-spin rounded-full border-2 border-blue-500 border-t-transparent"
            ></div>
          </div>

          <div
            v-else
            class="space-y-5"
          >
            <!-- Available Clerks -->
            <div class="space-y-2">
              <div class="flex items-center gap-2">
                <Users class="h-4 w-4 text-gray-600" />
                <h4 class="text-sm font-semibold text-gray-900">{{ t('availableClerks') }}</h4>
                <span class="rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-500">
                  {{ availableClerks.length }}
                </span>
              </div>
              <div
                v-if="availableClerks.length > 0"
                class="max-h-40 space-y-0 overflow-y-auto rounded-lg border border-gray-200"
              >
                <div
                  v-for="clerk in availableClerks"
                  :key="clerk.uuid"
                  class="border-b border-gray-100 p-2.5 transition-colors last:border-b-0 hover:bg-gray-50"
                >
                  <div class="text-sm font-medium text-gray-900">
                    {{ `${clerk.first_name || ''} ${clerk.last_name || ''}`.trim() || clerk.email }}
                  </div>
                </div>
              </div>
              <div
                v-else
                class="rounded-lg border border-gray-200 bg-gray-50/30 py-4 text-center text-sm italic text-gray-400"
              >
                {{ t('noClerksAvailable') }}
              </div>
            </div>
          </div>
        </div>

        <!-- Booking Links -->
        <div
          v-if="selectedCapability === AI_CAPABILITIES.SEND_BOOKING_LINKS"
          class="space-y-4"
        >
          <div class="space-y-3 text-sm text-gray-600">
            <p>
              {{ t('bookingLinksInstruction') }}
            </p>

            <div class="space-y-2 rounded-md bg-gray-50 p-3 text-xs">
              <p class="font-medium">{{ t('exampleBookingInheritance') }}</p>

              <hr class="border-gray-300" />

              <p class="font-medium">{{ t('exampleBookingEmployment') }}</p>

              <hr class="border-gray-300" />

              <p class="font-medium">{{ t('exampleBookingGeneral') }}</p>
            </div>
          </div>

          <div
            v-if="isLoadingBookingData"
            class="flex justify-center py-4"
          >
            <div
              class="h-6 w-6 animate-spin rounded-full border-2 border-blue-500 border-t-transparent"
            ></div>
          </div>

          <div
            v-else
            class="space-y-5"
          >
            <!-- Available Services -->
            <div class="space-y-2">
              <div class="flex items-center gap-2">
                <Calendar class="h-4 w-4 text-gray-600" />
                <h4 class="text-sm font-semibold text-gray-900">{{ t('availableServices') }}</h4>
                <span class="rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-500">
                  {{ availableServices.length }}
                </span>
              </div>
              <div
                v-if="availableServices.length > 0"
                class="max-h-40 space-y-0 overflow-y-auto rounded-lg border border-gray-200"
              >
                <div
                  v-for="service in availableServices"
                  :key="service.uuid"
                  class="border-b border-gray-100 p-2.5 transition-colors last:border-b-0 hover:bg-gray-50"
                >
                  <div class="text-sm font-medium text-gray-900">{{ service.name }}</div>
                </div>
              </div>
              <div
                v-else
                class="rounded-lg border border-gray-200 bg-gray-50/30 py-4 text-center text-sm italic text-gray-400"
              >
                {{ t('noServicesAvailable') }}
              </div>
            </div>

            <!-- Available Connected Calendars -->
            <div class="space-y-2">
              <div class="flex items-center gap-2">
                <Link class="h-4 w-4 text-gray-600" />
                <h4 class="text-sm font-semibold text-gray-900">
                  {{ t('availableConnectedCalendars') }}
                </h4>
                <span class="rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-500">
                  {{ availableCalendars.length }}
                </span>
              </div>
              <div
                v-if="availableCalendars.length > 0"
                class="max-h-40 space-y-0 overflow-y-auto rounded-lg border border-gray-200"
              >
                <div
                  v-for="calendar in availableCalendars"
                  :key="calendar.uuid"
                  class="border-b border-gray-100 p-2.5 transition-colors last:border-b-0 hover:bg-gray-50"
                >
                  <div class="text-sm font-medium text-gray-900">{{ calendar.name }}</div>
                </div>
              </div>
              <div
                v-else
                class="rounded-lg border border-gray-200 bg-gray-50/30 py-4 text-center text-sm italic text-gray-400"
              >
                {{ t('noCalendarsAvailable') }}
              </div>
            </div>
          </div>
        </div>

        <!-- Email Templates -->
        <div
          v-if="selectedCapability === AI_CAPABILITIES.SEND_EMAIL_TEMPLATES"
          class="space-y-4"
        >
          <div class="space-y-3 text-sm text-gray-600">
            <p>
              {{ t('naturalLanguageInstruction') }}
            </p>

            <div class="space-y-2 rounded-md bg-gray-50 p-3 text-xs">
              <p class="font-medium">{{ t('exampleStandard') }}</p>

              <hr class="border-gray-300" />

              <p class="font-medium">{{ t('exampleRejection') }}</p>

              <hr class="border-gray-300" />

              <p class="font-medium">{{ t('exampleLaborLaw') }}</p>
            </div>
          </div>

          <div
            v-if="isLoadingData"
            class="flex justify-center py-4"
          >
            <div
              class="h-6 w-6 animate-spin rounded-full border-2 border-blue-500 border-t-transparent"
            ></div>
          </div>

          <div
            v-else
            class="space-y-5"
          >
            <!-- Available Email Templates -->
            <div class="space-y-2">
              <div class="flex items-center gap-2">
                <svg
                  class="h-4 w-4 text-gray-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M3 8l7.89 7.89a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                <h4 class="text-sm font-semibold text-gray-900">
                  {{ t('availableEmailTemplates') }}
                </h4>
                <span class="rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-500">
                  {{ emailTemplates.length }}
                </span>
              </div>
              <div
                v-if="emailTemplates.length > 0"
                class="max-h-40 space-y-0 overflow-y-auto rounded-lg border border-gray-200"
              >
                <div
                  v-for="template in emailTemplates"
                  :key="template.uuid"
                  class="border-b border-gray-100 p-2.5 transition-colors last:border-b-0 hover:bg-gray-50"
                >
                  <div class="flex items-center gap-2">
                    <div class="flex-1 truncate text-sm font-medium text-gray-900">
                      {{ template.name }}
                    </div>
                    <span
                      v-if="template.type"
                      class="flex-shrink-0 whitespace-nowrap rounded-md px-1.5 py-0.5 text-xs"
                      :class="
                        template.type === 'create'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      "
                    >
                      {{ template.type === 'create' ? 'Mandatsaufnahme' : 'Mandatsablehnung' }}
                    </span>
                  </div>
                </div>
              </div>
              <div
                v-else
                class="rounded-lg border border-gray-200 bg-gray-50/30 py-4 text-center text-sm italic text-gray-400"
              >
                {{ t('noEmailTemplatesAvailable') }}
              </div>
            </div>

            <!-- Available Questionnaires -->
            <div class="space-y-2">
              <div class="flex items-center gap-2">
                <svg
                  class="h-4 w-4 text-gray-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                  />
                </svg>
                <h4 class="text-sm font-semibold text-gray-900">
                  {{ t('availableQuestionnaires') }}
                </h4>
                <span class="rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-500">
                  {{ allQuestionnaires.length }}
                </span>
              </div>
              <div
                v-if="allQuestionnaires.length > 0"
                class="max-h-40 space-y-0 overflow-y-auto rounded-lg border border-gray-200"
              >
                <div
                  v-for="questionnaire in allQuestionnaires"
                  :key="questionnaire.uuid"
                  class="border-b border-gray-100 p-2.5 transition-colors last:border-b-0 hover:bg-gray-50"
                >
                  <div class="text-sm font-medium text-gray-900">{{ questionnaire.title }}</div>
                </div>
              </div>
              <div
                v-else
                class="rounded-lg border border-gray-200 bg-gray-50/30 py-4 text-center text-sm italic text-gray-400"
              >
                {{ t('noQuestionnairesAvailable') }}
              </div>
            </div>

            <!-- Available PDF Documents -->
            <div class="space-y-2">
              <div class="flex items-center gap-2">
                <svg
                  class="h-4 w-4 text-gray-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <h4 class="text-sm font-semibold text-gray-900">
                  {{ t('availablePdfDocuments') }}
                </h4>
                <span class="rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-500">
                  {{ allForms.length }}
                </span>
              </div>
              <div
                v-if="allForms.length > 0"
                class="max-h-40 space-y-0 overflow-y-auto rounded-lg border border-gray-200"
              >
                <div
                  v-for="form in allForms"
                  :key="form.id"
                  class="border-b border-gray-100 p-2.5 transition-colors last:border-b-0 hover:bg-gray-50"
                >
                  <div class="text-sm font-medium text-gray-900">{{ form.title }}</div>
                </div>
              </div>
              <div
                v-else
                class="rounded-lg border border-gray-200 bg-gray-50/30 py-4 text-center text-sm italic text-gray-400"
              >
                {{ t('noPdfDocumentsAvailable') }}
              </div>
            </div>
          </div>
        </div>

        <!-- Create Documents -->
        <div
          v-if="selectedCapability === AI_CAPABILITIES.CREATE_DOCUMENTS"
          class="space-y-4"
        >
          <div class="space-y-3 text-sm text-gray-600">
            <p>
              {{ t('documentCreationInstruction') }}
            </p>

            <div class="space-y-2 rounded-md bg-gray-50 p-3 text-xs">
              <p class="font-medium">{{ t('exampleCreateInsuranceLetter') }}</p>

              <hr class="border-gray-300" />

              <p class="font-medium">{{ t('exampleCreateEmploymentDocs') }}</p>
            </div>
          </div>

          <div
            v-if="isLoadingDocumentData"
            class="flex justify-center py-4"
          >
            <div
              class="h-6 w-6 animate-spin rounded-full border-2 border-blue-500 border-t-transparent"
            ></div>
          </div>

          <div
            v-else
            class="space-y-5"
          >
            <!-- Available Word Document Templates -->
            <div class="space-y-2">
              <div class="flex items-center gap-2">
                <FileText class="h-4 w-4 text-gray-600" />
                <h4 class="text-sm font-semibold text-gray-900">
                  {{ t('availableWordDocumentTemplates') }}
                </h4>
                <span class="rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-500">
                  {{ availableWordDocumentTemplates.length }}
                </span>
              </div>
              <div
                v-if="availableWordDocumentTemplates.length > 0"
                class="max-h-40 space-y-0 overflow-y-auto rounded-lg border border-gray-200"
              >
                <div
                  v-for="template in availableWordDocumentTemplates"
                  :key="template.uuid"
                  class="border-b border-gray-100 p-2.5 transition-colors last:border-b-0 hover:bg-gray-50"
                >
                  <div class="text-sm font-medium text-gray-900">{{ template.name }}</div>
                </div>
              </div>
              <div
                v-else
                class="rounded-lg border border-gray-200 bg-gray-50/30 py-4 text-center text-sm italic text-gray-400"
              >
                {{ t('noWordDocumentTemplatesAvailable') }}
              </div>
            </div>
          </div>
        </div>

        <!-- Change Status -->
        <div
          v-if="selectedCapability === AI_CAPABILITIES.CHANGE_STATUS"
          class="space-y-4"
        >
          <p class="text-sm text-gray-600">
            {{ t(`capabilities.${AI_CAPABILITIES.CHANGE_STATUS}.description`) }}
          </p>
          <div class="rounded-md bg-gray-50 p-3">
            <div class="mb-2 text-sm font-medium">
              {{ t(`capabilities.${AI_CAPABILITIES.CHANGE_STATUS}.automationsTitle`) }}
            </div>
            <div class="space-y-1 text-xs">
              <div class="flex justify-between">
                <span>
                  {{ t(`capabilities.${AI_CAPABILITIES.CHANGE_STATUS}.rules.newToInProgress`) }}
                </span>
                <span class="text-blue-600">
                  {{
                    t(`capabilities.${AI_CAPABILITIES.CHANGE_STATUS}.rules.newToInProgressTrigger`)
                  }}
                </span>
              </div>
              <div class="flex justify-between">
                <span>
                  {{ t(`capabilities.${AI_CAPABILITIES.CHANGE_STATUS}.rules.inProgressToWaiting`) }}
                </span>
                <span class="text-orange-600">
                  {{
                    t(
                      `capabilities.${AI_CAPABILITIES.CHANGE_STATUS}.rules.inProgressToWaitingTrigger`
                    )
                  }}
                </span>
              </div>
              <div class="flex justify-between">
                <span>
                  {{ t(`capabilities.${AI_CAPABILITIES.CHANGE_STATUS}.rules.waitingToClosed`) }}
                </span>
                <span class="text-green-600">
                  {{
                    t(`capabilities.${AI_CAPABILITIES.CHANGE_STATUS}.rules.waitingToClosedTrigger`)
                  }}
                </span>
              </div>
            </div>
          </div>
          <div class="mt-4 flex gap-2">
            <Button
              size="sm"
              variant="outline"
            >
              {{ t(`capabilities.${AI_CAPABILITIES.CHANGE_STATUS}.buttons.editRules`) }}
            </Button>
            <Button
              size="sm"
              variant="ghost"
            >
              {{ t(`capabilities.${AI_CAPABILITIES.CHANGE_STATUS}.buttons.showHistory`) }}
            </Button>
          </div>
        </div>
      </div>
    </DialogContent>
  </Dialog>
</template>
